import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnChanges,
  ViewChild,
} from '@angular/core';
import imageCompression from 'browser-image-compression';
import { IComboBase } from '../../../interfaces/base/base.interface';
import { CompanyService } from '../../../services/company.service';
import { EmployeeService } from '../../../services/employee.service';
import { DialogService } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';
import { CurrencyService } from '../../../services/currency.service';
import { CountryService } from '../../../services/country.service';
import { CityService } from '../../../services/city.service';
import { TypeDocumentService } from '../../../services/type-document.service';
import { JobPositionService } from '../../../services/job-position.service';
import { WorkingModeService } from '../../../services/working-modes.service';
import { ModalLoadingComponent } from '../../../shared/modals/modal-loading/modal-loading.component';
import { EmployeeLocalService } from '../../services/employee.service';
import { ModalSuccessComponent } from '../../../shared/modals/modal-success/modal-success.component';
import { ModalRejectComponent } from '../../../shared/modals/modal-reject/modal-reject.component';
import { TypeContractService } from '../../../services/type-contracts.service';
import { ContinuityContractsService } from '../../../services/continuity-contracts.service';

@Component({
  selector: 'app-cesados-edit-modal',
  templateUrl: './cesados-edit-modal.component.html',
  styleUrls: ['./cesados-edit-modal.component.scss'],
  providers: [DialogService],
})
export class CesadosEditModalComponent implements OnChanges {

  @Input() visible: boolean = false;
  @Input() dataToEdit: any | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() editComplete = new EventEmitter<void>();
  @Output() openContractModal = new EventEmitter<any>();
  
  @ViewChild('imageUploader')
  imageUploader: any;


  // =========================
  // CONSTANTS
  // =========================

  readonly WORKING_MODE = {
    REMOTE: '1',
    PRESENTIAL: '2',
    HYBRID: '3',
  };

  readonly EMPLOYEE_STATUS = {
    ACTIVE: '1',
    INACTIVE: '2',
  };


  // =========================
  // UI STATE
  // =========================

  isLoading: boolean = false;
  
  isDisabledEmployee: boolean = true;

  showMotivo: boolean = true;


  // =========================
  // COMBOS
  // =========================
  
  filteredOptions: IComboBase[] = [];
  
  companies: IComboBase[] = [];
  countries: IComboBase[] = [];
  cities: IComboBase[] = [];

  typeDocuments: IComboBase[] = [];
  statusEmployee: IComboBase[] = [];
  genders: IComboBase[] = [];

  currencies: IComboBase[] = [];

  jobPositions: IComboBase[] = [];
  typeContracts: IComboBase[] = [];
  workingModes: IComboBase[] = [];


  // =========================
  // FORM
  // =========================
  
  selectedCompany: IComboBase | null = null;
  selectedJobPosition: IComboBase | null = null;
  selectedCountry: IComboBase | null = null;
  selectedCity: IComboBase | null = null;

  selectedTypeDocument: IComboBase | null = null;
  selectedStatusEmployee: IComboBase | null = null;
  selectedGender: IComboBase | null = null;

  name: string | null = null;
  lastname: string | null = null;

  document: string | null = null;
  
  phone: string | null = null;
  phoneEmergency: string | null = null;
  
  address: string | null = null;
  email: string | null = null;

  birthDate: Date | null = null;
  startDate: Date | null = null;
  terminationDate: Date | null = null;

  selectedPhoneCode: string | null = null;

  selectedMotivo: string = '';
  motivoOtro: string = '';

  maxLength: number | null = null;

  selectedWorkingMode: IComboBase | null = null;


  // =========================
  // FILES
  // =========================

  selectedPhoto: string | null = null;
  currentPhoto: File | null = null;
  

  // =========================
  // VALIDATIONS
  // =========================

  hasInteracted: { [key: string]: boolean } = {
    company: false,
    typeDocument: false,
    document: false,
    selectedPhoto: false,
    name: false,
    lastname: false,
    country: false,
    city: false,
    phone: false,
    email: false,
    address: false,
    phoneEmergency: false,
    statusEmployee: false,
    gender: false,
    birthDate: false,
    startDate: false,
    currentPhoto: false,
  };

  constructor(
    private employeeLocalService: EmployeeLocalService,
    private currencyService: CurrencyService,
    private companyService: CompanyService,
    private employeeService: EmployeeService,
    private countryService: CountryService,
    private cityService: CityService,
    private typeDocumentService: TypeDocumentService,
    private jobPositionService: JobPositionService,
    private workingModeService: WorkingModeService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(): void {
    if (this.visible && this.dataToEdit) {
      this.isLoading = true;

      const employeeId = this.dataToEdit.employeeId;

      this.employeeService.getEmployeeById(employeeId).subscribe({
        next: (employee) => {
          const countryId = employee.countryId;

          forkJoin({
            companies: this.companyService.comboCompany(),
            country: this.countryService.comboCountry(),
            city: this.cityService.comboCity(countryId),
            currencies: this.currencyService.comboCurrency(),
            jobPositions: this.jobPositionService.comboJobPosition(),
            workingModes: this.workingModeService.comboWorkingMode(),
            typeDocuments: this.typeDocumentService.comboTypeDocument(countryId),
          }).subscribe({
            next: (data) => {
              this.companies = data.companies;
              this.countries = data.country;
              this.cities = data.city;
              this.currencies = data.currencies;
              this.jobPositions = data.jobPositions;
              this.workingModes = data.workingModes;
              this.typeDocuments = data.typeDocuments;

              this.statusEmployee = [
                { key: '1', text: 'Activo' },
                { key: '2', text: 'Inactivo' },
              ];

              this.genders = [
                { key: '1', text: 'Femenino' },
                { key: '2', text: 'Masculino' },
              ];

              this.selectedCompany =
                this.companies.find(
                  (c) => c.key.toString() === employee.companyId.toString()
                ) || null;

              this.selectedCountry =
                this.countries.find(
                  (c) => c.key.toString() === employee.countryId.toString()
                ) || null;

              this.selectedCity =
                this.cities.find(
                  (c) => c.key.toString() === employee.cityId.toString()
                ) || null;

              this.selectedJobPosition =
                this.jobPositions.find(
                  (c) => c.key.toString() === employee.jobPositionId.toString()
                ) || null;

              this.selectedWorkingMode =
                this.workingModes.find(
                  (t) => t.key.toString() === employee.workingModeId.toString()
                ) || null;

              this.selectedTypeDocument =
                this.typeDocuments.find(
                  (t) => t.key.toString() === employee.typedocumentid.toString()
                ) || null;

              if (this.selectedTypeDocument?.key.toString() === '1') {
                this.maxLength = 8;
              } else {
                this.maxLength = null;
              }

              type Gender = 'F' | 'M';

              const genderMap: Record<Gender, string> = {
                F: '1',
                M: '2',
              };

              const genderKey = genderMap[employee.gender as Gender];

              this.selectedGender =
                this.genders.find((g) => g.key === genderKey) || null;

              const statusKey = employee.employeeState
                ? this.EMPLOYEE_STATUS.ACTIVE
                : this.EMPLOYEE_STATUS.INACTIVE;

              this.selectedStatusEmployee =
                this.statusEmployee.find((s) => s.key === statusKey) || null;

              const motivosFijos = [
                'Por despido',
                'Mutuo disenso',
                'Renuncia',
                'Finalización de Contrato',
              ];

              const motivo = employee.reasonInactive;

              if (motivosFijos.includes(motivo)) {
                this.selectedMotivo = motivo;
                this.motivoOtro = '';
              } else {
                this.selectedMotivo = 'Otro';
                this.motivoOtro = motivo;
              }
              
              this.name = employee.names;
              this.lastname = employee.lastNames;
              this.document = employee.employeeDocumentNumber;
              this.selectedPhoneCode = employee.phoneCode;
              this.phone = employee.phone;
              this.phoneEmergency = employee.phoneEmergency;
              this.address = employee.address;
              this.email = employee.email;

              this.birthDate = this.parseDate(employee.birthDate);
              this.startDate = this.parseDate(employee.startDate);

              this.terminationDate = employee.terminationDate
                ? this.parseDate(employee.terminationDate)
                : null;

              this.selectedPhoto = employee.imageEmployee;

              this.isLoading = false;
              this.validateFormEmployee();
              this.cdr.detectChanges();
            },
            error: (err) => {
              this.isLoading = false;
              console.error('Error al cargar las listas de opciones:', err);
            },
          });
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Error al cargar los datos del empleado:', err);
        },
      });
    }
  }


  // =========================
  // HELPERS
  // =========================

  private parseDate(date: string): Date {
    const [day, month, year] = date.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  private isValidEmail(email: string | null): boolean {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }


  // =========================
  // FILTERS
  // =========================

  filterOptions(event: any, type: string) {
    const query = event.query.toLowerCase();

    let options: IComboBase[] = [];
    
    switch (type) {
      case 'company':
        options = this.companies;
        break;

      case 'country':
        options = this.countries;
        break;

      case 'city':
        options = this.cities;
        break;

      case 'typeDocument':
        options = this.typeDocuments;
        break;

      case 'statusEmployee':
        options = this.statusEmployee;
        break;

      case 'gender':
        options = this.genders;
        break;

      case 'typeContract':
        options = this.typeContracts;
        break;

      case 'jobPosition':
        options = this.jobPositions;
        break;
      
      case 'workingMode':
        options = this.workingModes;
        break;

      default:
        console.error('Tipo no reconocido:', type);
        return;
    }

    this.filteredOptions = options.filter((option) =>
      option.text.toLowerCase().includes(query)
    );
  }


  // =========================
  // INPUT EVENTS
  // =========================

  onChangeInput(event: any, type: string) {
    this.hasInteracted[type] = true;

    switch (type) {
      case 'company':
        this.selectedCompany = null;
        break;

      case 'country':
        this.selectedCountry = null;
        break;

      case 'city':
        this.selectedCity = null;
        break;

      case 'typeDocument':
        this.selectedTypeDocument = null;
        break;

      case 'statusEmployee':
        this.selectedStatusEmployee = null;
        break;

      case 'gender':
        this.selectedGender = null;
        break;

      case 'birthDate':
        this.birthDate = event;
        break;

      case 'startDate':
        this.startDate = event;
        break;

      case 'name':
        this.name = event;
        break;

      case 'lastname':
        this.lastname = event;
        break;

      case 'address':
        this.address = event;
        break;

      case 'email':
        this.email = event;
        break;

      case 'phone':
        this.phone = event;
        break;

      case 'phoneEmergency':
        this.phoneEmergency = event;
        break;

      case 'document':
        this.document = event;
        break;

      default:
        console.error('Tipo no reconocido:', type);
        return;
    }
    
    this.validateFormEmployee();
  }


  // =========================
  // SELECT EVENTS
  // =========================

  onSelect(event: any, type: string) {
    this.hasInteracted[type] = true;

    switch (type) {
      case 'company':
        this.selectedCompany = event.value ?? null;
        break;

      case 'country':
        this.selectedCountry = event.value ?? null;
        this.selectedPhoneCode = event.value?.phonecode ?? null;
      
        this.selectedCity = null;
        this.selectedTypeDocument = null;

        const countryId = this.selectedCountry?.key;

        if (countryId) {
          this.cityService.comboCity(countryId).subscribe({
            next: (data) => {
              this.cities = data;
            },
            error: (err) => {
              console.error('Error al obtener la lista de ciudades:', err);
            },
          });

          this.typeDocumentService.comboTypeDocument(countryId).subscribe({
            next: (data) => {
              this.typeDocuments = data;
            },
            error: (err) => {
              console.error('Error al obtener los tipos de documentos:', err);
            },
          });
        }
        break;

      case 'city':
        this.selectedCity = event.value ?? null;
        break;

      case 'typeDocument':
        this.selectedTypeDocument = event.value ?? null;

        if (this.selectedTypeDocument?.key.toString() === '1') {
          this.maxLength = 8;
        } else {
          this.maxLength = null;
          this.document = null;
        }
        break;

      case 'statusEmployee':
        this.selectedStatusEmployee = event.value ?? null;
        break;

      case 'gender':
        this.selectedGender = event.value ?? null;
        break;

      case 'jobPosition':
        this.selectedJobPosition = event.value ?? null;
        break;

      case 'workingMode':
        this.selectedWorkingMode = event.value ?? null;
        break;

      default:
        console.error('Tipo no reconocido:', type);
        return;
    }

    this.validateFormEmployee();
  }


  // =========================
  // VALIDATIONS
  // =========================

  validateField(field: string) {
    this.hasInteracted[field] = true;
  }

  validateFormEmployee() {
    const isInactive = this.selectedStatusEmployee?.key === this.EMPLOYEE_STATUS.INACTIVE;

    const isEmployeeFormValid = !!(
      this.selectedCompany &&
      this.selectedTypeDocument &&
      this.document &&
      this.birthDate &&
      this.startDate &&
      this.selectedGender &&
      this.name &&
      this.lastname &&
      this.selectedCountry &&
      this.selectedCity &&
      this.phone &&
      this.isValidEmail(this.email) &&
      this.address &&
      this.phoneEmergency &&
      this.selectedStatusEmployee &&
      (!isInactive || this.terminationDate)
    );

    this.isDisabledEmployee = !(
      isEmployeeFormValid &&
      this.selectedStatusEmployee?.key === this.EMPLOYEE_STATUS.ACTIVE
    );
  }


  // =========================
  // IMAGE
  // =========================

  onPhotoSelect(event: any) {
    const file = event.files[0];

    if (!file) return;

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      this.dialogService.open(ModalRejectComponent, {
        header: '',
        data: {
          text: 'La imagen es demasiado grande. El tamaño máximo permitido es de 10 MB.',
          title: '¡Upss!',
          showButton: true,
          buttonText: 'Entendido',
        },
      });

      this.validateFormEmployee();
      return;
    }

    this.currentPhoto = file;
    
    const reader = new FileReader();
    
    reader.onload = (e: any) => {
      this.selectedPhoto = e.target.result;
      this.validateFormEmployee();
    };
    
    reader.readAsDataURL(file);
  }

  openFileUpload(type: string) {
    if (type === 'image' && this.imageUploader) {
      this.imageUploader.choose();
    } else {
      console.error('El fileUploader no está disponible.');
    }
  }

  editPhoto() {
    this.openFileUpload('image');
    this.validateFormEmployee();
  }

  addPhoto() {
    this.openFileUpload('image');
  }

  removePhoto() {
    this.selectedPhoto = null;
    this.currentPhoto = null;
    this.validateFormEmployee();
  }

  async compressImage(file: File, maxSizeMB: number): Promise<File> {
    if (file.size / (1024 * 1024) <= maxSizeMB) return file;

    const options = {
      maxSizeMB,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };

    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error('Error al comprimir la imagen:', error);
      return file;
    }
  }


  // =========================
  // UTILS
  // =========================

  onlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) event.preventDefault();
  }

  goToContractStep(): void {
    const reasonInactive = this.selectedMotivo === 'Otro' ? this.motivoOtro : this.selectedMotivo;
    const employeeRequest = {
      employeeId: this.dataToEdit.employeeId,
      companyId: this.selectedCompany?.key,
      typeDocumentId: this.selectedTypeDocument?.key,
      employeeDocumentNumber: this.document,
      names: this.name,
      lastNames: this.lastname,
      countryId: this.selectedCountry?.key,
      cityId: this.selectedCity?.key,
      address: this.address,
      email: this.email,
      phone: this.phone?.toString(),
      phoneEmergency: this.phoneEmergency?.toString(),
      employeeState: (
        this.selectedStatusEmployee?.key === this.EMPLOYEE_STATUS.ACTIVE
          ? true
          : false
      ).toString(),
      reasonInactive: reasonInactive,
      gender: this.selectedGender?.key === '1' ? 'F' : 'M',
      birthDate: this.birthDate?.toISOString().replace('Z', ''),
      startDate: this.startDate?.toISOString().replace('Z', ''),
      terminationDate: this.terminationDate
        ? this.terminationDate.toISOString().replace('Z', '')
        : null,
      jobPositionId: this.selectedJobPosition?.key,
      workingModeId: this.selectedWorkingMode?.key,
    };

    // console.log('Employee request:', employeeRequest);

    this.openContractModal.emit(employeeRequest);

    this.closeDialog();
  }


  // =========================
  // MODAL
  // ========================='

  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }
}
